import { useState, useRef, useEffect, type ReactNode } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Plus, X, Check,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   DataPagination — Paginação avançada reutilizável
   
   Features (todas configuráveis):
   - Seletor de limite (badges 25/50/100/200 + custom + Todos)
   - Página atual com dropdown para navegação direta
   - Navegação anterior/próximo + primeiro/último
   
   Uso simples:
     <DataPagination page={1} totalPages={5} onPageChange={setPage} />

   Uso completo:
     <DataPagination
       page={page}
       totalPages={totalPages}
       total={1001}
       limite={25}
       onPageChange={setPage}
       onLimiteChange={setLimite}
       showLimiteSelector
       showPageDropdown
       showFirstLast
     />
   ═══════════════════════════════════════════════════════════════ */

/** Opções padrão de limite */
const DEFAULT_LIMITES = [25, 50, 100, 200];

/** Limite máximo para exibir opção "Todos" */
const MAX_TODOS = 10000;

export interface DataPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;

  /** Total de registros (necessário para "Todos" e seletor de limite) */
  total?: number;
  /** Limite atual por página */
  limite?: number;
  /** Callback ao mudar limite */
  onLimiteChange?: (limite: number) => void;

  // ── Feature toggles ──
  /** Exibe seletor de limite (Exibir: 25 50 100 200 [+]) */
  showLimiteSelector?: boolean;
  /** Exibe dropdown de navegação direta para página */
  showPageDropdown?: boolean;
  /** Exibe botões |< e >| (ir ao primeiro/último) */
  showFirstLast?: boolean;
  /** Exibe botões Anterior/Próximo */
  showPrevNext?: boolean;
  /** Permite usuário adicionar limite customizado via [+] */
  allowCustomLimite?: boolean;
  /** Exibe opção "Todos" (só se total <= 10.000) */
  allowTodos?: boolean;
  /** Opções de limite customizadas (substitui DEFAULT_LIMITES) */
  limiteOptions?: number[];

  /** Custom info text (overrides default "Página X de Y") */
  infoText?: string;
  /** Extra content */
  extra?: ReactNode;
  /** Custom className */
  className?: string;
}

export function DataPagination({
  page,
  totalPages,
  onPageChange,
  total,
  limite,
  onLimiteChange,
  showLimiteSelector = false,
  showPageDropdown = false,
  showFirstLast = false,
  showPrevNext = true,
  allowCustomLimite = true,
  allowTodos = true,
  limiteOptions,
  infoText,
  extra,
  className = '',
}: DataPaginationProps) {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [showPageDD, setShowPageDD] = useState(false);
  const customInputRef = useRef<HTMLInputElement>(null);
  const pageDDRef = useRef<HTMLDivElement>(null);

  const LIMITES = limiteOptions || DEFAULT_LIMITES;

  // ── Auto-focus custom input ──
  useEffect(() => {
    if (showCustomInput && customInputRef.current) {
      customInputRef.current.focus();
      customInputRef.current.select();
    }
  }, [showCustomInput]);

  // ── Close page dropdown on click outside ──
  useEffect(() => {
    if (!showPageDD) return;
    const handler = (e: MouseEvent) => {
      if (pageDDRef.current && !pageDDRef.current.contains(e.target as Node)) {
        setShowPageDD(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPageDD]);

  const handleOpenCustom = () => {
    // Pre-fill com último valor × 2
    const lastLimite = limite || LIMITES[LIMITES.length - 1];
    setCustomValue(String(lastLimite * 2));
    setShowCustomInput(true);
  };

  const handleConfirmCustom = () => {
    const val = parseInt(customValue, 10);
    if (val > 0 && val <= 99999 && onLimiteChange) {
      onLimiteChange(val);
    }
    setShowCustomInput(false);
  };

  const handleCustomKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleConfirmCustom();
    if (e.key === 'Escape') setShowCustomInput(false);
  };

  const handleLimiteSelect = (val: number) => {
    if (onLimiteChange) {
      onLimiteChange(val);
      onPageChange(1);
    }
  };

  const canShowTodos = allowTodos && total != null && total <= MAX_TODOS;

  // ── Gerar lista de páginas para o dropdown ──
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <div className={`dt-pagination dt-pagination-advanced ${className}`}>

      {/* ═══ LEFT: Seletor de limite ═══ */}
      {showLimiteSelector && onLimiteChange ? (
        <div className="dt-pg-limits">
          <span className="dt-pg-limits-label">Exibir:</span>
          <div className="dt-pg-limits-badges">
            {LIMITES.map(val => (
              <button
                key={val}
                className={`dt-pg-limit-badge ${limite === val ? 'dt-pg-limit-active' : ''}`}
                onClick={() => handleLimiteSelect(val)}
              >
                {val}
              </button>
            ))}

            {/* Todos */}
            {canShowTodos && (
              <button
                className={`dt-pg-limit-badge ${limite === total ? 'dt-pg-limit-active' : ''}`}
                onClick={() => handleLimiteSelect(total!)}
              >
                Todos
              </button>
            )}

            {/* Custom input ou botão + */}
            {allowCustomLimite && !showCustomInput && (
              <button
                className="dt-pg-limit-badge dt-pg-limit-add"
                onClick={handleOpenCustom}
                title="Personalizar quantidade"
              >
                <Plus size={12} />
              </button>
            )}

            {showCustomInput && (
              <div className="dt-pg-custom-input-wrap">
                <input
                  ref={customInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={5}
                  value={customValue}
                  onChange={e => setCustomValue(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={handleCustomKeyDown}
                  className="dt-pg-custom-input"
                />
                <button className="dt-pg-custom-btn dt-pg-custom-ok" onClick={handleConfirmCustom}>
                  <Check size={12} />
                </button>
                <button className="dt-pg-custom-btn dt-pg-custom-cancel" onClick={() => setShowCustomInput(false)}>
                  <X size={12} />
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="dt-pg-limits" />
      )}

      {/* ═══ CENTER: Página X (dropdown) ═══ */}
      <div className="dt-pg-center">
        {showPageDropdown && totalPages > 1 ? (
          <div className="dt-pg-page-dd-wrap" ref={pageDDRef}>
            <button
              className="dt-pg-page-dd-trigger"
              onClick={() => setShowPageDD(!showPageDD)}
            >
              {infoText || `Página ${page}`}
            </button>
            {showPageDD && (
              <div className="dt-pg-page-dd-menu">
                <div className="dt-pg-page-dd-scroll">
                  {pageList.map(p => (
                    <button
                      key={p}
                      className={`dt-pg-page-dd-item ${p === page ? 'dt-pg-page-dd-active' : ''}`}
                      onClick={() => { onPageChange(p); setShowPageDD(false); }}
                    >
                      Página {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <span className="dt-page-info">
            {infoText || `Página ${page} de ${totalPages}`}
          </span>
        )}
      </div>

      {/* ═══ RIGHT: Navegação ═══ */}
      <div className="dt-page-buttons">
        {extra}

        {showFirstLast && (
          <button
            className="dt-page-btn dt-page-btn-icon"
            onClick={() => onPageChange(1)}
            disabled={page <= 1}
            title="Primeira página"
          >
            <ChevronsLeft size={15} />
          </button>
        )}

        {showPrevNext && (
          <button
            className="dt-page-btn"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
          >
            <ChevronLeft size={14} /> Anterior
          </button>
        )}

        {showPrevNext && (
          <button
            className="dt-page-btn"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
          >
            Próximo <ChevronRight size={14} />
          </button>
        )}

        {showFirstLast && (
          <button
            className="dt-page-btn dt-page-btn-icon"
            onClick={() => onPageChange(totalPages)}
            disabled={page >= totalPages}
            title="Última página"
          >
            <ChevronsRight size={15} />
          </button>
        )}
      </div>
    </div>
  );
}
